import React, { useCallback, useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';

import Form, { RequiredAsterisk } from '@atlaskit/form';
import { Box, Stack, Text, xcss } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';
import type { DocNode } from '@atlaskit/adf-schema';

import type {
	AvailableSite,
	IssueCreateEmbedSuccessPayload as SingleWorkCreateFormSuccessPayload,
} from '@atlassian/gic-anywhere';

import { fg } from '@confluence/feature-gating';

import { messages } from '../../messages';
import { saveCloudIdToLocalStorage } from '../../utils/localStorageHelpers';
import { useGetInitialDataForSingleIssueCreateForm } from '../../services/useGetInitialDataForSingleIssueCreateForm';
import { useIssueCreateSidePanelActions } from '../../providers/IssueCreateSidePanelContextProvider';
import { useCreateSingleWorkItem } from '../../services/useCreateSingleWorkItem';
import { useShouldUseGicEmbed } from '../../services/useShouldUseGicEmbed';
import { IssueTypeSelector } from '../work-create-form-common/IssueTypeSelector';
import { SiteSelector } from '../work-create-form-common/SiteSelector';
import { ProjectSelector } from '../work-create-form-common/ProjectSelector';
import type { JiraProject } from '../../__types__/apiUtils';
import { constructJiraIssueUrl } from '../../services/issueInsertionUtils';

import { SingleWorkCreateFormFooter } from './SingleWorkCreateFormFooter';

const singleWorkCreateFormStyles = xcss({
	flexGrow: 1,
	flexBasis: '200px',
	overflowY: 'auto',
});

const singleWorkCreateFormContainerStyles = xcss({
	height: '100%',
	paddingTop: 'space.200',
});

// Props definition for SingleWorkCreateForm
export interface SingleWorkCreateFormProps {
	closePanel?: () => void;
	summary?: string;
	descriptionAdf?: DocNode;
	currentSummary?: string;
	currentDescription?: DocNode;
	onShouldUseEmbed?: (shouldUse: {
		selectedSite: AvailableSite | null;
		selectedProject: JiraProject | null;
	}) => void;
	onDescriptionRequiredChange?: (isDescriptionRequired: boolean) => void;
}

// This is the view for both AI and non-AI single work create where users can edit and create individual issues
export const SingleWorkCreateForm = ({
	onSuccess,
	closePanel = () => {},
	aiSupportedSection,
	currentSummary = '',
	currentDescription,
	onShouldUseEmbed,
	onDescriptionRequiredChange,
}: {
	onSuccess: (payload: SingleWorkCreateFormSuccessPayload) => void;
	closePanel?: () => void;
	aiSupportedSection: React.ReactNode;
	currentSummary?: string;
	currentDescription?: DocNode;
	onShouldUseEmbed?: (shouldUse: {
		selectedSite: AvailableSite | null;
		selectedProject: JiraProject | null;
	}) => void;
	onDescriptionRequiredChange?: (isDescriptionRequired: boolean) => void;
}) => {
	const { setCurrentSiteSelection } = useIssueCreateSidePanelActions();
	const { formatMessage } = useIntl();

	// State for form fields
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submissionError, setSubmissionError] = useState<string | null>(null);

	// Get initial data for the form
	const {
		// Site picker data
		availableSites,
		selectedSite,
		isSitePickerLoading,
		// Project picker data
		selectedProject,
		optionsRecentProjects,
		optionsAllProjects,
		isProjectPickerLoading,
		// Issue type picker data
		selectedIssueType,
		optionsIssueTypes,
		isIssueTypePickerLoading,
		// Error handling
		error: formError,
		// Change handlers
		onSiteChange,
		onProjectChange,
		onIssueTypeChange,
		onProjectSelectorQueryChange,
	} = useGetInitialDataForSingleIssueCreateForm();

	// Get GIC embed configuration
	const {
		shouldUseEmbed,
		isLoading: isGicEmbedCheckLoading,
		lightGicRequiredFields,
	} = useShouldUseGicEmbed({
		selectedSite,
		selectedProject,
		selectedIssueType,
	});

	const isDescriptionRequired =
		lightGicRequiredFields?.some((field) => field.key === 'description') || false;

	useEffect(() => {
		onDescriptionRequiredChange?.(isDescriptionRequired);
	}, [isDescriptionRequired, onDescriptionRequiredChange]);

	const {
		createSingleWorkItemHandler,
		loading: isCreating,
		error: createError,
	} = useCreateSingleWorkItem();

	// Notify parent when embed should be used
	useEffect(() => {
		if (shouldUseEmbed) {
			onShouldUseEmbed?.({
				selectedSite,
				selectedProject,
			});
		}
	}, [shouldUseEmbed, onShouldUseEmbed, selectedSite, selectedProject]);

	const handleSubmit = useCallback(async () => {
		if (
			isSubmitting ||
			!selectedSite ||
			!selectedProject ||
			!selectedIssueType ||
			!currentSummary ||
			(isDescriptionRequired && !currentDescription?.content?.length)
		)
			return;

		try {
			setIsSubmitting(true);
			setSubmissionError(null);

			if (selectedSite) {
				void setCurrentSiteSelection(selectedSite.cloudId);
				saveCloudIdToLocalStorage({ selectedCloudId: selectedSite.cloudId });
			}

			const { isSuccess, response } = await createSingleWorkItemHandler({
				cloudId: selectedSite.cloudId,
				issueBody: {
					fields: {
						summary: currentSummary,
						description: currentDescription,
						project: {
							id: selectedProject.id,
						},
						issuetype: {
							id: selectedIssueType.id,
						},
					},
				},
			});
			if (isSuccess) {
				if (response) {
					const successPayload: SingleWorkCreateFormSuccessPayload = {
						issueKey: response.key,
						link: constructJiraIssueUrl(selectedSite.url, response.key),
						summary: currentSummary,
						selectedCloudId: selectedSite.cloudId,
						issueId: Number(response.id),
					};
					onSuccess(successPayload);
				}
				closePanel();
			}
		} catch (_error: any) {
			setSubmissionError(
				_error?.errors?.description || formatMessage(messages.singleWorkCreateFormJiraErrorText),
			);
		} finally {
			setIsSubmitting(false);
		}
	}, [
		isSubmitting,
		selectedSite,
		selectedProject,
		selectedIssueType,
		createSingleWorkItemHandler,
		currentSummary,
		currentDescription,
		setCurrentSiteSelection,
		closePanel,
		onSuccess,
		formatMessage,
		isDescriptionRequired,
	]);

	const onClose = useCallback(() => {
		closePanel();
	}, [closePanel]);

	const isSaveDisabled =
		isSubmitting ||
		isCreating ||
		isSitePickerLoading ||
		isProjectPickerLoading ||
		isIssueTypePickerLoading ||
		isGicEmbedCheckLoading ||
		!selectedSite ||
		!selectedProject ||
		!selectedIssueType ||
		!currentSummary?.trim() ||
		(isDescriptionRequired && !currentDescription?.content?.length);

	const error = submissionError || createError || formError;

	return (
		<Stack testId="single-work-create-form-wrapper" xcss={singleWorkCreateFormContainerStyles}>
			<Box paddingInline="space.250" xcss={singleWorkCreateFormStyles}>
				<Stack space="space.050">
					<Text size="small" color="color.text.accent.gray">
						{formatMessage(
							messages.workCreateCommonRequiredFieldsSectionMessageNoteWithoutTailingAsterisk,
						)}
						<RequiredAsterisk />
					</Text>
				</Stack>
				<Stack space="space.100">
					{error && (
						<SectionMessage appearance="error" testId="singleWorkCreateFormErrorSectionMessage">
							{typeof error === 'string' ? (
								error
							) : (
								<FormattedMessage {...messages.singleWorkCreateFormJiraErrorText} />
							)}
						</SectionMessage>
					)}
					<Stack space="space.300">
						<Form onSubmit={handleSubmit}>
							{({ formProps }) => (
								<form
									id="single-work-create-form"
									data-testid="single-work-create-form"
									{...formProps}
								>
									<Stack space="space.100">
										<SiteSelector
											options={availableSites}
											value={selectedSite}
											onChange={onSiteChange}
											isLoading={isSitePickerLoading}
											isDisabled={isSitePickerLoading || isSubmitting || isCreating}
											isRequired
											label={formatMessage(messages.singleWorkCreateFormSite)}
										/>
										<ProjectSelector
											optionsAllProjects={optionsAllProjects}
											optionsRecentProjects={optionsRecentProjects}
											onInputChange={onProjectSelectorQueryChange}
											value={selectedProject}
											onChange={onProjectChange}
											isLoading={isProjectPickerLoading}
											isDisabled={isSubmitting || isCreating}
											label={formatMessage(messages.singleWorkCreateFormProject)}
											labelProjectSelectorRecentProjects={formatMessage(
												messages.singleWorkCreateFormProjectSelectorRecentProjects,
											)}
											labelProjectSelectorProjects={formatMessage(
												messages.singleWorkCreateFormProjectSelectorProjects,
											)}
											isRequired
										/>
										<IssueTypeSelector
											optionsIssueTypes={optionsIssueTypes}
											value={selectedIssueType}
											onChange={onIssueTypeChange}
											isLoading={isIssueTypePickerLoading}
											isDisabled={
												isIssueTypePickerLoading || !selectedProject || isSubmitting || isCreating
											}
											label={
												fg('confluence-issue-terminology-refresh')
													? formatMessage(
															messages.singleWorkCreateFormDefaultIssueTypeIssueTermRefresh,
														)
													: formatMessage(messages.singleWorkCreateFormDefaultIssueType)
											}
											isRequired
										/>
										{aiSupportedSection}
									</Stack>
								</form>
							)}
						</Form>
					</Stack>
				</Stack>
			</Box>
			<SingleWorkCreateFormFooter
				onClose={onClose}
				closePanel={closePanel}
				isSaveDisabled={isSaveDisabled}
			/>
		</Stack>
	);
};
